/**
 * OneDrive sign-in, on @azure/msal-browser v2.
 *
 * What was wrong with v1, from MOBILE-IOS.md section 1b:
 *
 * 1. v1 renewed tokens with a hidden iframe against login.microsoftonline.com.
 *    Safari's ITP blocks third-party cookies in that iframe, so
 *    acquireTokenSilent *always* failed on iOS - not intermittently, always.
 *    The "We still sometimes see the constant reauth problem" comment that
 *    used to sit at the top of oneDriveApi.ts was this.
 * 2. The fallback was loginPopup, called from an async catch block. No user
 *    activation survives an await, so Safari blocked the popup, and in a
 *    home-screen PWA a popup could not have delivered a result anyway.
 *
 * v2 fixes the first outright: it is authorisation code + PKCE, and it renews
 * from a refresh token in localStorage with a direct call to the token
 * endpoint. No iframe, no third-party cookies, nothing for ITP to block.
 *
 * The second is fixed here rather than by the library: interaction is never
 * started from inside a token request (see MSALAuthenticationProvider), and
 * where a popup cannot deliver we navigate the whole page instead.
 *
 * ## Why v2 and not v3
 *
 * MODERNIZATION.md named v3 as the target, and v3 was tried first. **webpack 4
 * cannot parse it.** Seventeen of the sixty-eight `.mjs` files in v3's bundle
 * use optional chaining, and webpack 4.42 parses with a nested **acorn 6.4.2**
 * that predates it - `ecmaVersion: 11` in webpack's parser options is a
 * request acorn 6 cannot honour. Babel does not save you either: CRA 3's rule
 * for node_modules leaves `?.` intact, which is how the failure presents:
 *
 *     ./node_modules/@azure/msal-browser/dist/telemetry/BrowserPerformanceClient.mjs
 *     Module parse failed: Unexpected token (20:39)
 *
 * v2's bundle parses clean - zero of fifty-six files - and gives us everything
 * this change is actually about: auth code + PKCE, refresh tokens in
 * localStorage, redirect flow, no iframes. The differences that matter are
 * small and noted at their call sites.
 *
 * **Move to v3 when the Vite migration lands** (MODERNIZATION.md §2). Rollup
 * has no such parser limit, and the API differences are minor: v3 drops
 * `logoutRedirect`-as-cache-clear in favour of a real `clearCache()`.
 */

import {
  AccountInfo,
  Configuration,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';
import { shouldUseRedirect } from '../../utilities/platform';
import { hasUserActivation, popupBlockedMessage } from '../../utilities/popupBlocked';
import { microsoftClientId } from '../../config';

export const scopes = ['User.Read', 'Files.ReadWrite'];

/**
 * Must be registered in the Azure app registration under the
 * **Single-page application** platform - not "Web". Microsoft treats a Web
 * redirect URI as belonging to a confidential client, so the PKCE exchange
 * this flow makes is refused with AADSTS70002 "must include a client_secret" -
 * which reads like an architectural problem and is a platform-type checkbox.
 *
 * CLAUDE.md "Where it runs" has both registrations, both client IDs and both
 * redirect URIs, and the story of the original directory-less production app.
 *
 * The path changed from /app/onedrive: that was the app proper, so completing
 * a sign-in meant booting the entire service tree first. This is a thin route
 * that does one thing, matching /google-redirect and /dropbox-redirect.
 */
export const redirectPath = '/onedrive-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

const msalConfiguration: Configuration = {
  auth: {
    /**
     * One registration per deployment (VITE_MICROSOFT_CLIENT_ID); a
     * second one for localhost goes in .env.development.local. Approot is
     * bound to the client id, so changing it moves every user's app folder -
     * see CLAUDE.md, "Registrations, the lessons".
     */
    clientId: microsoftClientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: redirectUri(),
    /**
     * We navigate back ourselves, from oneDriveRedirect.
     *
     * Left on, MSAL bounces off the redirect route back to the originating URL
     * and expects handleRedirectPromise to be called a second time there, on a
     * page that has meanwhile booted the whole app. Two call sites, one of
     * them racing startup. Doing it once, here, and then navigating is what
     * the Google and Dropbox flows in this codebase already do.
     */
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    /**
     * v1 needed this because its hidden-iframe renewal had to carry state
     * across a third-party context. v3 does not renew that way, and the cookie
     * is one more thing for ITP to delete.
     */
    storeAuthStateInCookie: false,
  },
};

/**
 * The one instance.
 *
 * v1 carried a warning about never creating a second UserAgentApplication -
 * it caused null-authority cache entries and duplicate hash listeners. v3 is
 * better behaved, but a second instance would still keep its own in-memory
 * view of a shared cache, so the rule stands.
 *
 * v3 also requires initialize() to have resolved before any other call, which
 * is why this hands back a promise rather than the instance.
 */
export const getMsalApp = (function () {
  let initialised: Promise<PublicClientApplication> | undefined;

  return function () {
    if (!initialised) {
      const app = new PublicClientApplication(msalConfiguration);

      initialised = app.initialize().then(function ready() {
        if (process.env.NODE_ENV === 'development') {
          (window as any).msal = app;
        }

        return app;
      });
    }

    return initialised;
  };
})();

/**
 * The account MSAL is currently working on behalf of, if any.
 *
 * getActiveAccount is only populated once something has set it, so fall back
 * to the cache. There is only ever one account here - the app signs in to one
 * OneDrive at a time.
 */
export function currentAccount(app: PublicClientApplication): AccountInfo | null {
  return app.getActiveAccount() || app.getAllAccounts()[0] || null;
}

export function adoptAccount(app: PublicClientApplication, account: AccountInfo | null) {
  if (account) {
    app.setActiveAccount(account);
  }

  return account;
}

/**
 * Signs out locally, without sending the user to Microsoft to sign out.
 *
 * v3 has `clearCache()` for exactly this. v2 does not, and its only cache-
 * clearing entry points are the logout flows - which navigate away to end the
 * session at the identity provider, tearing the app down to service a button
 * that means "stop using my OneDrive here". `onRedirectNavigate` returning
 * false is the documented way to take the cache clearing without the
 * navigation.
 *
 * Note it deliberately leaves the Microsoft session alone. Logging out of
 * Fadila should not log you out of Outlook in the next tab.
 */
export async function clearLocalSession() {
  const app = await getMsalApp();
  const account = currentAccount(app);

  app.setActiveAccount(null);

  await app.logoutRedirect({
    account: account || undefined,
    onRedirectNavigate: () => false,
  });
}

/* -------------------------------------------------------------------------
 * Carrying intent across the redirect
 * ---------------------------------------------------------------------- */

export interface OneDriveIntent {
  /** Make OneDrive the storage provider once a token lands. */
  configureProvider?: boolean;
}

interface RedirectState {
  returnTo: string;
  intent: OneDriveIntent;
  createdAt: number;
}

const stateKey = 'oneDriveRedirectState';

/** A round trip that has taken longer than this is not one we still expect. */
const stateLifetimeMs = 10 * 60 * 1000;

/**
 * localStorage, and not MSAL's own `state` field on the request.
 *
 * MSAL will carry a state string for you, but it hands it back only through
 * the AuthenticationResult on the redirect route - and the thing that needs
 * the intent is OneDriveService, which does not exist until we have navigated
 * away from that route. The value has to outlive the page either way, so it
 * may as well live somewhere both pages can read.
 *
 * sessionStorage would be wrong for the same reason gisRedirect gives: iOS may
 * relaunch the app to service the hop, and a relaunch starts a fresh session.
 */
function rememberState(intent?: OneDriveIntent) {
  try {
    localStorage.setItem(
      stateKey,
      JSON.stringify({
        // Come back to wherever the user actually was, not a fixed landing page.
        returnTo: window.location.pathname + window.location.search,
        intent: intent || {},
        createdAt: Date.now(),
      } as RedirectState),
    );
  } catch {
    // Private browsing. The sign-in still completes; we just land on /app
    // rather than exactly where the user was.
  }
}

/** Reads the state and forgets it. Acting on it twice would be wrong. */
export function takeState(): RedirectState {
  const empty: RedirectState = { returnTo: '/app', intent: {}, createdAt: 0 };

  try {
    const raw = localStorage.getItem(stateKey);

    localStorage.removeItem(stateKey);

    if (!raw) {
      return empty;
    }

    const parsed = JSON.parse(raw) as RedirectState;

    if (!parsed || Date.now() - parsed.createdAt > stateLifetimeMs) {
      return empty;
    }

    return {
      returnTo: parsed.returnTo || '/app',
      intent: parsed.intent || {},
      createdAt: parsed.createdAt,
    };
  } catch {
    return empty;
  }
}

/* -------------------------------------------------------------------------
 * Starting the flow
 * ---------------------------------------------------------------------- */

/**
 * Whether this device needs the whole page to go to Microsoft.
 *
 * Exported so callers can tell an authorize() that will return from one that
 * navigates away and never returns at all.
 */
export function willRedirect(allowRedirect?: boolean) {
  return !!allowRedirect && shouldUseRedirect();
}

/**
 * Navigates away. Nothing after this resolves, by design.
 */
export async function beginRedirect(intent?: OneDriveIntent) {
  const app = await getMsalApp();

  rememberState(intent);

  const request: RedirectRequest = { scopes };

  await app.loginRedirect(request);
}

/**
 * The desktop path.
 *
 * Unlike Dropbox this needs no about:blank trick: MSAL opens the window
 * itself, and it is reached directly from the click rather than from behind
 * an await we control. `await getMsalApp()` does resolve first, but only the
 * once - by the time any sign-in button is on screen the service has long
 * since initialised the instance, so the promise is already settled and the
 * open still happens in the same task as the gesture.
 */
export async function beginPopup() {
  const hadGesture = hasUserActivation();
  const app = await getMsalApp();

  let result;

  try {
    result = await app.loginPopup({ scopes });
  } catch (err) {
    // MSAL reports a blocked window as popup_window_error, and one that never
    // painted as empty_window_error. Both reach the snackbar as raw library
    // prose - "Error opening popup window. This can happen if you are using
    // IE or if popups are blocked in the browser." - which names a browser
    // nobody here is running and gives no idea where the setting lives. Say
    // the same thing every other provider says instead.
    const code = (err as { errorCode?: string })?.errorCode;

    if (code === 'popup_window_error' || code === 'empty_window_error') {
      throw new Error(popupBlockedMessage('OneDrive', hadGesture));
    }

    throw err;
  }

  return adoptAccount(app, result.account);
}

/* -------------------------------------------------------------------------
 * Finishing the flow
 * ---------------------------------------------------------------------- */

export class OneDriveAuthError extends Error {
  constructor(
    message: string,
    public readonly returnTo: string,
  ) {
    super(message);
    this.name = 'OneDriveAuthError';
  }
}

export interface OneDriveAuthResult {
  account: AccountInfo | null;
  returnTo: string;
  intent: OneDriveIntent;
}

/**
 * Completes the redirect. Runs on /onedrive-redirect and nowhere else.
 *
 * The intent is deliberately *not* consumed here - OneDriveService picks it up
 * once it mounts, on the other side of the navigation. What is returned is a
 * copy, for the error path.
 */
export async function completeRedirect(): Promise<OneDriveAuthResult> {
  const app = await getMsalApp();
  const { returnTo, intent } = peekState();

  let result;

  try {
    result = await app.handleRedirectPromise();
  } catch (err) {
    // Whatever went wrong, the state is spent; leaving it would strand the
    // next attempt behind a stale returnTo.
    takeState();

    throw new OneDriveAuthError((err as Error).message || 'OneDrive sign-in failed.', returnTo);
  }

  if (!result) {
    takeState();

    throw new OneDriveAuthError(
      'OneDrive sign-in could not be completed - there was no response to read.',
      returnTo,
    );
  }

  return {
    account: adoptAccount(app, result.account),
    returnTo,
    intent,
  };
}

/** Reads the state without consuming it. */
function peekState(): RedirectState {
  try {
    const raw = localStorage.getItem(stateKey);

    if (!raw) {
      return { returnTo: '/app', intent: {}, createdAt: 0 };
    }

    const parsed = JSON.parse(raw) as RedirectState;

    return {
      returnTo: (parsed && parsed.returnTo) || '/app',
      intent: (parsed && parsed.intent) || {},
      createdAt: (parsed && parsed.createdAt) || 0,
    };
  } catch {
    return { returnTo: '/app', intent: {}, createdAt: 0 };
  }
}
